import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Crown, SlidersHorizontal, X } from 'lucide-react';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import RecentlyViewed from '../components/RecentlyViewed';
import SEOHead from '../components/SEOHead';
import BreadcrumbSchema from '../components/BreadcrumbSchema';
import { trackSearch } from '../utils/analytics';

const API = `${window.location.origin}/api`;

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Hydrate from localStorage cache so the product grid appears INSTANTLY on return visits.
  const hydrate = (key) => { try { const r = localStorage.getItem(key); return r ? JSON.parse(r) : null; } catch { return null; } };
  const persist = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch {} };
  const [allProducts, setAllProducts] = useState(() => hydrate('jsf_shop_products_cache') || []);
  const [categories, setCategories] = useState(() => hydrate('jsf_shop_categories_cache') || []);
  const [loading, setLoading] = useState(() => !hydrate('jsf_shop_products_cache'));
  const [search, setSearch] = useState('');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const activeFilter = searchParams.get('filter') || searchParams.get('gender') || 'All';

  // Multi-select category filter state — comma-separated in URL
  const categoryParam = searchParams.get('categories') || '';
  const selectedCategories = categoryParam ? categoryParam.split(',').filter(Boolean) : [];

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/products`),
      axios.get(`${API}/categories`),
    ]).then(([prodRes, catRes]) => {
      setAllProducts(prodRes.data);
      setCategories(catRes.data || []);
      setLoading(false);
      persist('jsf_shop_products_cache', prodRes.data);
      persist('jsf_shop_categories_cache', catRes.data || []);
    }).catch(() => setLoading(false));
  }, []);

  const setFilter = (f) => {
    const next = new URLSearchParams(searchParams);
    if (f === 'All') next.delete('filter');
    else next.set('filter', f);
    next.delete('gender');
    setSearchParams(next);
  };

  const updateCategories = (newCats) => {
    const next = new URLSearchParams(searchParams);
    if (newCats.length === 0) next.delete('categories');
    else next.set('categories', newCats.join(','));
    setSearchParams(next);
  };

  const toggleCategory = (cat) => {
    if (selectedCategories.includes(cat)) {
      updateCategories(selectedCategories.filter(c => c !== cat));
    } else {
      updateCategories([...selectedCategories, cat]);
    }
  };

  const selectSingleCategory = (cat) => {
    if (cat === 'All') updateCategories([]);
    else if (selectedCategories.length === 1 && selectedCategories[0] === cat) updateCategories([]);
    else updateCategories([cat]);
  };

  const clearAllFilters = () => {
    setSearchParams({});
    setSearch('');
  };

  // Apply filters
  let filtered = allProducts;
  if (activeFilter === 'Men') filtered = filtered.filter(p => p.gender === 'Men');
  else if (activeFilter === 'Women') filtered = filtered.filter(p => p.gender === 'Women');
  else if (activeFilter === 'Unisex') filtered = filtered.filter(p => p.gender === 'Unisex');
  else if (activeFilter === 'Signature') filtered = filtered.filter(p => p.is_signature);

  if (selectedCategories.length > 0) {
    filtered = filtered.filter(p => selectedCategories.includes(p.category || 'Perfume'));
  }

  // Debounced Meta Pixel Search event — fire once the user stops typing for
  // 700ms and has at least 2 chars, so we don't spam per-keystroke events.
  useEffect(() => {
    const q = (search || '').trim();
    if (q.length < 2) return;
    const t = setTimeout(() => trackSearch(q), 700);
    return () => clearTimeout(t);
  }, [search]);

  if (search) {
    filtered = filtered.filter(p =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      (p.description || '').toLowerCase().includes(search.toLowerCase())
    );
  }

  const PAGE_TITLES = {
    All: 'All Fragrances',
    Men: 'For Him',
    Women: 'For Her',
    Unisex: 'Unisex',
    Signature: 'Signature Creations',
  };

  // Build category list (always include defaults if products use them)
  const allCategoryOptions = Array.from(new Set([...(categories || []), ...allProducts.map(p => p.category).filter(Boolean)])).sort();

  const hasActiveFilters = activeFilter !== 'All' || selectedCategories.length > 0 || search;

  const CategorySidebar = () => (
    <aside className="space-y-6" data-testid="category-sidebar">
      <div>
        <h3 className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-sans mb-4">Category</h3>
        <div className="space-y-2.5">
          {allCategoryOptions.length === 0 && (
            <p className="text-white/30 text-xs">No categories yet</p>
          )}
          {allCategoryOptions.map(cat => {
            const checked = selectedCategories.includes(cat);
            const count = allProducts.filter(p => (p.category || 'Perfume') === cat).length;
            return (
              <label
                key={cat}
                className="flex items-center justify-between cursor-pointer group"
                data-testid={`category-checkbox-${cat.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <span className="flex items-center gap-3">
                  <span className={`w-4 h-4 border flex items-center justify-center transition-colors ${
                    checked ? 'bg-[#1C7D3F] border-[#1C7D3F]' : 'border-white/30 group-hover:border-white/60'
                  }`}>
                    {checked && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleCategory(cat)}
                    className="sr-only"
                  />
                  <span className={`text-sm font-sans ${checked ? 'text-white' : 'text-white/60 group-hover:text-white'}`}>
                    {cat}
                  </span>
                </span>
                <span className="text-xs text-white/30 font-sans">{count}</span>
              </label>
            );
          })}
        </div>
      </div>

      {hasActiveFilters && (
        <button
          onClick={clearAllFilters}
          data-testid="clear-filters-btn"
          className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] hover:text-white transition-colors font-sans border-b border-[#D4AF37]/40 hover:border-white pb-1"
        >
          Clear All Filters
        </button>
      )}
    </aside>
  );

  return (
    <div data-testid="shop-page" className="min-h-screen">
      <SEOHead
        title="Shop All Fragrances"
        description="Browse the full JS Fragrance catalog — Eau de Parfum for men, women, and unisex. Hand-blended in the Philippines, delivered nationwide. Filter by gender, scent family, or category."
        url="https://jsfragrances.com/shop"
      />
      <BreadcrumbSchema crumbs={[{ name: 'Home', path: '/' }, { name: 'Shop', path: '/shop' }]} />
      {/* Header */}
      <div className="bg-[#0A0A0A] py-12 sm:py-16 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2 font-sans">
            {activeFilter === 'Signature' ? 'JS Originals' : 'Collection'}
          </p>
          <h1 className="text-4xl sm:text-5xl tracking-tight leading-none font-light text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {PAGE_TITLES[activeFilter] || 'All Fragrances'}
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Top filter bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex gap-2 flex-wrap" data-testid="gender-filters">
            {['All', 'Men', 'Women', 'Unisex'].map(g => (
              <button
                key={g}
                onClick={() => setFilter(g)}
                data-testid={`filter-${g.toLowerCase()}`}
                className={`px-5 py-2 text-xs uppercase tracking-[0.15em] font-sans transition-colors ${
                  activeFilter === g
                    ? 'bg-[#D4AF37] text-black font-medium'
                    : 'border border-white/20 text-white/60 hover:border-white/40'
                }`}
              >
                {g}
              </button>
            ))}
            <button
              onClick={() => setFilter('Signature')}
              data-testid="filter-signature"
              className={`px-5 py-2 text-xs uppercase tracking-[0.15em] font-sans transition-colors flex items-center gap-1.5 ${
                activeFilter === 'Signature'
                  ? 'bg-[#D4AF37] text-black font-medium'
                  : 'border border-[#D4AF37]/30 text-[#D4AF37] hover:border-[#D4AF37]/60'
              }`}
            >
              <Crown className="w-3 h-3" /> Signature
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setMobileFiltersOpen(true)}
              data-testid="mobile-filters-toggle"
              className="lg:hidden flex items-center gap-2 px-4 py-2 border border-white/20 text-white/70 text-xs uppercase tracking-[0.15em] hover:border-white/40 transition-colors"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              Filters {selectedCategories.length > 0 && `(${selectedCategories.length})`}
            </button>
            <div className="relative" data-testid="search-bar">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <input
                type="text"
                placeholder="Search fragrances..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full sm:w-64 pl-10 pr-4 py-2 bg-transparent border border-white/20 text-white text-sm focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-white/30"
                data-testid="search-input"
              />
            </div>
          </div>
        </div>

        {/* Category Pills Row */}
        {allCategoryOptions.length > 0 && (
          <div className="mb-8 -mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto" data-testid="category-pills-row">
            <div className="flex gap-2 min-w-max sm:flex-wrap pb-1">
              <button
                onClick={() => updateCategories([])}
                data-testid="category-pill-all"
                className={`px-4 py-1.5 text-[11px] uppercase tracking-[0.18em] font-sans whitespace-nowrap transition-colors rounded-full ${
                  selectedCategories.length === 0
                    ? 'bg-[#1C7D3F] text-white border border-[#1C7D3F]'
                    : 'border border-white/20 text-white/60 hover:border-[#1C7D3F]/60 hover:text-white'
                }`}
              >
                All Categories
              </button>
              {allCategoryOptions.map(cat => {
                const isActive = selectedCategories.length === 1 && selectedCategories[0] === cat;
                return (
                  <button
                    key={cat}
                    onClick={() => selectSingleCategory(cat)}
                    data-testid={`category-pill-${cat.replace(/\s+/g, '-').toLowerCase()}`}
                    className={`px-4 py-1.5 text-[11px] uppercase tracking-[0.18em] font-sans whitespace-nowrap transition-colors rounded-full ${
                      isActive
                        ? 'bg-[#1C7D3F] text-white border border-[#1C7D3F]'
                        : selectedCategories.includes(cat)
                          ? 'bg-[#1C7D3F]/20 text-white border border-[#1C7D3F]/60'
                          : 'border border-white/20 text-white/60 hover:border-[#1C7D3F]/60 hover:text-white'
                    }`}
                  >
                    {cat}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-[220px_1fr] gap-8">
          {/* Sidebar — desktop only */}
          <div className="hidden lg:block">
            <div className="sticky top-24">
              <CategorySidebar />
            </div>
          </div>

          {/* Products column */}
          <div>
            <p className="text-white/40 text-sm mb-6 font-sans">
              {filtered.length} fragrance{filtered.length !== 1 ? 's' : ''}
              {selectedCategories.length > 0 && (
                <span className="ml-2 text-white/30">· {selectedCategories.join(', ')}</span>
              )}
            </p>

            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-8">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-[#141414] aspect-[3/4] mb-3" />
                    <div className="h-3 bg-[#141414] w-1/3 mb-2" />
                    <div className="h-4 bg-[#141414] w-2/3 mb-2" />
                    <div className="h-3 bg-[#141414] w-1/4" />
                  </div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20" data-testid="empty-state">
                <p className="text-white/40 text-lg mb-4">No fragrances found</p>
                {hasActiveFilters && (
                  <button
                    onClick={clearAllFilters}
                    className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] hover:text-white transition-colors font-sans border-b border-[#D4AF37]/40 hover:border-white pb-1"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-8 stagger-children" data-testid="products-grid">
                {filtered.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-filter-drawer">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setMobileFiltersOpen(false)}
          />
          <div className="absolute right-0 top-0 bottom-0 w-80 max-w-[85vw] bg-[#0A0A0A] border-l border-white/10 p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-lg font-light text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Filters
              </h2>
              <button
                onClick={() => setMobileFiltersOpen(false)}
                data-testid="mobile-filter-close"
                className="text-white/60 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <CategorySidebar />
          </div>
        </div>
      )}

      {/* Recently Viewed strip — only renders if user has browsed before */}
      <RecentlyViewed />
    </div>
  );
}
